import { ProductAttributeOption, ProductAttributes } from '../types';

export interface NormalizedOption {
  name: string;
  extra_price: number;
}

/**
 * Normalizes an attribute option into { name, extra_price }
 */
export const normalizeOption = (opt: string | ProductAttributeOption): NormalizedOption => {
  if (!opt) return { name: '', extra_price: 0 };
  if (typeof opt === 'string') {
    return { name: opt.trim(), extra_price: 0 };
  }
  return {
    name: (opt.name || '').trim(),
    extra_price: Number(opt.extra_price ?? opt.price ?? 0),
  };
};

/**
 * Normalizes an array of options into an array of NormalizedOption
 */
export const normalizeOptions = (options?: (string | ProductAttributeOption)[]): NormalizedOption[] => {
  if (!Array.isArray(options)) return [];
  return options.map(normalizeOption).filter(o => o.name !== '');
};

/**
 * Formats extra price display (e.g. "+৳৩০০" or "+৳300")
 */
export const formatExtraPriceBadge = (extraPrice: number, lang: string = 'bn'): string => {
  if (!extraPrice || extraPrice <= 0) return '';
  const formatted = extraPrice.toLocaleString();
  return lang === 'bn' ? ` (+৳${formatted})` : ` (+৳${formatted})`;
};

/**
 * Calculates final unit price based on base price and all selected attribute options
 */
export const calculateProductPriceWithAttributes = (
  basePrice: number,
  attributes?: ProductAttributes,
  selectedColor?: string,
  selectedSize?: string,
  selectedWeight?: string,
  selectedCustom?: Record<string, string>
): number => {
  let total = Number(basePrice || 0);
  if (!attributes) return total;

  // Colors
  if (selectedColor && attributes.colors?.enabled) {
    const opts = normalizeOptions(attributes.colors.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedColor.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Sizes
  if (selectedSize && attributes.sizes?.enabled) {
    const opts = normalizeOptions(attributes.sizes.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedSize.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Weights
  if (selectedWeight && attributes.weights?.enabled) {
    const opts = normalizeOptions(attributes.weights.options);
    const found = opts.find(o => o.name.toLowerCase() === selectedWeight.toLowerCase());
    if (found) total += found.extra_price;
  }

  // Custom attributes (e.g. Hijab, Material, Warranty)
  if (selectedCustom && attributes.custom && Array.isArray(attributes.custom)) {
    for (const group of attributes.custom) {
      if (!group.enabled) continue;
      const groupKey = group.id || group.name;
      const selectedVal = selectedCustom[groupKey] || selectedCustom[group.name];
      if (selectedVal) {
        const opts = normalizeOptions(group.options);
        const found = opts.find(o => o.name.toLowerCase() === selectedVal.toLowerCase());
        if (found) total += found.extra_price;
      }
    }
  }

  return total;
};
